import {useMemo} from 'react'
import {useIntl} from 'react-intl'
import {Button, Empty, Flex, Space, Table, Tooltip, Typography} from 'antd'
import {CheckCircleFilled, CloseCircleFilled} from '@ant-design/icons'
import RecordsCount from '../common/RecordsCount'
import {ProgresSpinner} from '../common/ProgresSpinner'

const PromoteChangesTable = ({
  dataSource,
  onViewPromotedFiles = null,
  onManageFiles = null,
  onDeleteFiles = null,
  onSyncToGit = null,
  onDownloadLogs = null,
  canDeleteFiles = false,
  canSyncToGit = false,
}) => {
  const intl = useIntl()

  const columns = useMemo(
    () => [
      {
        title: '#',
        width: 60,
        align: 'center',
        render: (_, __, index) => index + 1,
      },
      {
        title: intl.formatMessage({ id: 'promoteChanges.tableVehicle' }),
        dataIndex: 'name',
        sorter: (a, b) =>
          `${a.name || ''} ${a.make_model_id || ''}`.localeCompare(
            `${b.name || ''} ${b.make_model_id || ''}`
          ),
        render: (_, record) => (
          <Flex align="center" gap={12}>
            {record.name} ({record.make_model_id})
            {record?.is_processing ? (
              <ProgresSpinner
                caption={intl.formatMessage({
                  id: 'promoteChanges.filesSyncing',
                })}
                size="small"
              />
            ) : record?.git_status === 'success' ? (
              <CheckCircleFilled style={{ color: 'green', fontSize: 20 }} />
            ) : record?.git_status === 'failure' ? (
              <Tooltip
                title={intl.formatMessage({
                  id: 'promoteChanges.failedToUpload',
                })}
              >
                <CloseCircleFilled style={{ color: 'red', fontSize: 20 }} />
              </Tooltip>
            ) : record?.git_status === 'cancelled' ? (
              <CloseCircleFilled style={{ color: 'gray', fontSize: 20 }} />
            ) : null}
          </Flex>
        ),
      },
      {
        title: intl.formatMessage({ id: 'promoteChanges.tableAction' }),
        align: 'right',
        width: '50%',
        render: (_, record) => {
          return (
            <Space size="middle" wrap>
              {canSyncToGit && record?.git_status === 'failure' && (
                <Button
                  type="link"
                  className="link-button"
                  onClick={() => onSyncToGit?.(record)}
                >
                  {intl.formatMessage({ id: 'promoteChanges.syncToGit' })}
                </Button>
              )}
              {record?.is_delete_exists && (
                <Button
                  type="link"
                  className="link-button"
                  onClick={() => onDownloadLogs?.(record)}
                >
                  {intl.formatMessage({
                    id: 'promoteChanges.downloadDeletedLogs',
                  })}
                </Button>
              )}
              <Button
                type="link"
                className="link-button"
                onClick={() => onViewPromotedFiles?.(record)}
              >
                {intl.formatMessage({ id: 'promoteChanges.seePromotedFiles' })}
              </Button>
              <Button
                type="link"
                className="link-button"
                onClick={() => onManageFiles?.(record)}
              >
                {intl.formatMessage({ id: 'promoteChanges.manageFileList' })}
              </Button>
              {canDeleteFiles && (
                <Button
                  type="link"
                  className="link-button"
                  onClick={() => onDeleteFiles?.(record)}
                >
                  {intl.formatMessage({ id: 'promoteChanges.deleteFiles' })}
                </Button>
              )}
            </Space>
          )
        },
      },
    ],
    [
      intl,
      onViewPromotedFiles,
      onManageFiles,
      onDeleteFiles,
      onSyncToGit,
      onDownloadLogs,
      canDeleteFiles,
      canSyncToGit,
    ]
  )

  return (
    <Flex style={{ width: '100%' }} vertical gap={16}>
      <Table
        rowKey={(record) => `promote-${record.vehicle_id}`}
        columns={columns}
        dataSource={dataSource}
        showSorterTooltip={false}
        pagination={false}
        scroll={{ y: '50vh' }}
        locale={{
          emptyText: (
            <div style={{ padding: '40px' }}>
              <Empty description={<RecordsCount count={0} />} />
            </div>
          ),
        }}
      />
      {dataSource?.length > 0 && (
        <Typography.Text>
          <RecordsCount count={dataSource.length} />
        </Typography.Text>
      )}
    </Flex>
  )
}

export default PromoteChangesTable
